import { Link, useParams } from 'react-router-dom';
import { MapPin, Tent, ArrowRight, ArrowLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SafetyPanel from '@/components/SafetyPanel';
import AccessibleParking from '@/components/AccessibleParking';
import { CAMPSITES } from '@/data/campsites';

const LIST_HERO =
  'https://images.unsplash.com/photo-1687876790754-2a92a8f0686d?fm=jpg&q=70&w=2000&auto=format&fit=crop';

/* /campsites — a directory of placeholder pitches. Each detail page
 * embeds the shared SafetyPanel with site-specific overrides. */

export function CampsitesIndex() {
  return (
    <>
      <Navigation />
      <main
        data-testid="campsites-page"
        className="bg-[var(--brand-parchment)] text-[var(--brand-forest)] pb-24"
      >
        {/* Hero */}
        <div className="relative h-[40svh] min-h-[300px] w-full overflow-hidden">
          <img
            src={LIST_HERO}
            alt="Warm sunrise over a Highland glen"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(4,20,10,0.55) 0%, rgba(4,20,10,0.35) 40%, rgba(4,20,10,0.88) 100%)',
            }}
          />
          <div className="absolute inset-x-0 bottom-0 max-w-4xl mx-auto px-6 lg:px-10 pb-10 text-[var(--brand-parchment)]">
            <span className="font-eyebrow !text-[var(--brand-gold)] inline-flex items-center gap-2">
              <Tent size={14} /> Campsites &amp; basecamps
            </span>
            <h1 className="mt-3 font-display text-5xl sm:text-6xl leading-[1.02] [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]">
              Pitches with a{' '}
              <span className="italic text-[var(--brand-gold)]">welcome.</span>
            </h1>
          </div>
        </div>

        {/* Intro */}
        <section className="max-w-3xl mx-auto px-6 lg:px-10 mt-14">
          <p className="text-lg text-[var(--brand-ink)] leading-relaxed">
            A short list of Highland pitches we&rsquo;ve ridden from, camped at,
            and would happily send our own family to. Each one has its own
            safety brief &mdash; mountain rescue team, nearest A&amp;E and where
            the phone signal lives &mdash; so you can settle in with a clear
            head.
          </p>
        </section>

        {/* Sites grid */}
        <section
          data-testid="campsites-grid"
          className="max-w-6xl mx-auto px-6 lg:px-10 mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {CAMPSITES.map((site) => (
            <Link
              key={site.slug}
              to={`/campsites/${site.slug}`}
              data-testid={`campsite-card-${site.slug}`}
              className="group bg-[var(--brand-surface)] border border-[var(--brand-olive)]/25 flex flex-col overflow-hidden transition-shadow duration-200 hover:shadow-[0_10px_32px_-16px_rgba(20,38,24,0.45)]"
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={site.image}
                  alt={site.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--brand-olive)]">
                  <MapPin size={12} /> {site.region}
                </div>
                <h2 className="mt-2 font-display text-2xl text-[var(--brand-forest-deep)]">
                  {site.name}
                </h2>
                <p className="mt-2 text-sm text-[var(--brand-muted)] leading-relaxed flex-1">
                  {site.tagline}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[var(--brand-forest)] link-expand">
                  Read the brief
                  <ArrowRight
                    size={14}
                    className="transition-transform duration-200 group-hover:translate-x-1"
                  />
                </span>
              </div>
            </Link>
          ))}
        </section>
      </main>
      <Footer />
    </>
  );
}

export function CampsiteDetail() {
  const { slug } = useParams();
  const site = CAMPSITES.find((c) => c.slug === slug);

  if (!site) {
    return (
      <>
        <Navigation />
        <main
          data-testid="campsite-not-found"
          className="min-h-[60vh] bg-[var(--brand-parchment)] flex items-center justify-center px-6"
        >
          <div className="max-w-md text-center">
            <span className="font-eyebrow !text-[var(--brand-olive)]">
              Campsite not found
            </span>
            <h1 className="mt-3 font-display text-3xl text-[var(--brand-forest-deep)]">
              We couldn&rsquo;t find that pitch.
            </h1>
            <p className="mt-3 text-sm text-[var(--brand-muted)]">
              It may have been moved or renamed. Head back to the directory for
              our current list.
            </p>
            <Link
              to="/campsites"
              className="btn-gold mt-6 inline-flex px-5 py-3 text-sm font-semibold items-center gap-2"
            >
              <ArrowLeft size={14} /> All campsites
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navigation />
      <main
        data-testid={`campsite-detail-${site.slug}`}
        className="bg-[var(--brand-parchment)] text-[var(--brand-forest)] pb-0"
      >
        {/* Hero */}
        <div className="relative h-[50svh] min-h-[360px] w-full overflow-hidden">
          <img
            src={site.image}
            alt={site.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(180deg, rgba(4,20,10,0.50) 0%, rgba(4,20,10,0.30) 40%, rgba(4,20,10,0.88) 100%)',
            }}
          />
          <div className="absolute inset-x-0 bottom-0 max-w-4xl mx-auto px-6 lg:px-10 pb-10 text-[var(--brand-parchment)]">
            <Link
              to="/campsites"
              data-testid="campsite-back-link"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[var(--brand-gold)] hover:text-[var(--brand-parchment)] transition-colors"
            >
              <ArrowLeft size={12} /> All campsites
            </Link>
            <span className="mt-4 font-eyebrow !text-[var(--brand-gold)] inline-flex items-center gap-2">
              <MapPin size={12} /> {site.region}
            </span>
            <h1 className="mt-2 font-display text-5xl sm:text-6xl leading-[1.02] [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]">
              {site.name}
            </h1>
            <p className="mt-3 max-w-xl text-base sm:text-lg text-[var(--brand-parchment)]/90 [text-shadow:0_1px_10px_rgba(0,0,0,0.5)]">
              {site.tagline}
            </p>
          </div>
        </div>

        {/* Description + features */}
        <section className="max-w-4xl mx-auto px-6 lg:px-10 mt-14 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <span className="font-eyebrow !text-[var(--brand-olive)]">
              About this pitch
            </span>
            <p className="mt-4 text-base text-[var(--brand-ink)] leading-relaxed">
              {site.description}
            </p>
          </div>
          <div>
            <span className="font-eyebrow !text-[var(--brand-olive)]">
              On site
            </span>
            <ul
              data-testid="campsite-features"
              className="mt-4 space-y-2 text-sm text-[var(--brand-ink)]"
            >
              {site.features.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--brand-gold)] shrink-0"
                  />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Divider */}
        <div className="hairline max-w-4xl mx-auto mt-14 opacity-40" />

        {/* Site-specific safety panel */}
        <div className="mt-2">
          <SafetyPanel site={site} />
        </div>

        {/* Accessible parking locator */}
        <AccessibleParking site={site} />
      </main>
      <Footer />
    </>
  );
}
